import type { OrganisationStructure } from '@/services/organisationStructureService';

export const DISPLAY_ROLES = {
  MANAGING_DIRECTOR: 'Managing Director',
  PORTFOLIO_DIRECTOR: 'Portfolio Director',
  DEPARTMENT_DIRECTOR: 'Department Director',
  TEAM_LEAD: 'Team Lead',
} as const;

const EXECUTIVE_SEAT_TEAM_NOS = ['0_0', '4_0', '5_0', '6_0'] as const;

const ROLE_STRENGTH: Record<string, number> = {
  [DISPLAY_ROLES.MANAGING_DIRECTOR]: 4,
  [DISPLAY_ROLES.PORTFOLIO_DIRECTOR]: 3,
  [DISPLAY_ROLES.DEPARTMENT_DIRECTOR]: 2,
  [DISPLAY_ROLES.TEAM_LEAD]: 1,
  '': 0,
};

export type OrgPreviewNodeType =
  | 'root'
  | 'executive'
  | 'directorSeat'
  | 'department'
  | 'team'
  | 'employee';

export interface OrgPreviewItem {
  id: string;
  name: string;
  children: string[];
  nodeType: OrgPreviewNodeType;
  subtitle?: string;
  derivedRole?: string;
  employeeType?: string;
  specialRole?: string;
  sourceRowId?: string;
  leadName?: string;
}

export interface OrganisationPreviewTree {
  rootItemId: string;
  items: Record<string, OrgPreviewItem>;
  activeRowCount: number;
  inactiveRowCount: number;
}

export interface BuildOrganisationPreviewOptions {
  asOf?: Date;
  includeInactive?: boolean;
}

export const DEPARTMENT_TEAM_THRESHOLD = 7;

export function departmentHasTeams(departmentNo: string): boolean {
  const n = Number(departmentNo);
  return !Number.isNaN(n) && n >= DEPARTMENT_TEAM_THRESHOLD;
}

export function isEmptyPk(value: string | null | undefined): boolean {
  const trimmed = value?.trim() ?? '';
  return trimmed === '' || trimmed === '-';
}

export function isDirectorSeat(teamNo: string | null | undefined): boolean {
  const trimmed = teamNo?.trim() ?? '';
  return trimmed.endsWith('_0');
}

export function isExecutiveSeat(teamNo: string | null | undefined): boolean {
  const trimmed = teamNo?.trim() ?? '';
  return (EXECUTIVE_SEAT_TEAM_NOS as readonly string[]).includes(trimmed);
}

export function deriveDisplayRole(row: OrganisationStructure): string {
  const employeeNo = row.employee_no?.trim() ?? '';
  const departmentPk = row.department_pk?.trim() ?? '';
  const teamPk = row.team_pk?.trim() ?? '';
  const teamNo = row.team_no?.trim() ?? '';

  if (isEmptyPk(teamPk)) {
    if (employeeNo !== departmentPk || isEmptyPk(departmentPk)) return '';
    if (teamNo === '0_0') return DISPLAY_ROLES.MANAGING_DIRECTOR;
    if (teamNo === '4_0' || teamNo === '5_0' || teamNo === '6_0') {
      return DISPLAY_ROLES.PORTFOLIO_DIRECTOR;
    }
    return DISPLAY_ROLES.DEPARTMENT_DIRECTOR;
  }

  if (employeeNo === teamPk) return DISPLAY_ROLES.TEAM_LEAD;
  return '';
}

export function mergeRoles(roles: string[]): string {
  let best = '';
  let bestStrength = 0;
  for (const role of roles) {
    const strength = ROLE_STRENGTH[role] ?? 0;
    if (strength > bestStrength) {
      bestStrength = strength;
      best = role;
    }
  }
  return best;
}

export function isRowActive(row: OrganisationStructure, asOf = new Date()): boolean {
  const today = asOf.toISOString().slice(0, 10);
  const endDate = row.end_date?.slice(0, 10);
  if (endDate && endDate < today) return false;
  const startDate = row.start_date?.slice(0, 10);
  if (startDate && startDate > today) return false;
  return true;
}

function compareSortKey(a: string, b: string): number {
  const numA = Number(a);
  const numB = Number(b);
  if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA - numB;
  return a.localeCompare(b);
}

function resolveDepartmentLeadPk(rows: OrganisationStructure[], departmentNo: string): string {
  const pks = rows
    .filter((row) => (row.department_no?.trim() ?? '') === departmentNo)
    .map((row) => row.department_pk?.trim() ?? '')
    .filter((pk) => !isEmptyPk(pk));
  if (pks.length === 0) return '';
  return [...pks].sort()[0] ?? '';
}

export function resolveTeamLeadPk(
  rows: OrganisationStructure[],
  departmentNo: string,
  teamNo: string
): string {
  const scoped = rows.filter(
    (row) =>
      (row.department_no?.trim() ?? '') === departmentNo &&
      (row.team_no?.trim() ?? '') === teamNo &&
      !isDirectorSeat(row.team_no)
  );
  const teamPks = scoped.map((row) => row.team_pk?.trim() ?? '').filter((pk) => !isEmptyPk(pk));
  if (teamPks.length > 0) return [...teamPks].sort()[0] ?? '';

  const departmentLeadPk = resolveDepartmentLeadPk(rows, departmentNo);
  if (isEmptyPk(departmentLeadPk)) return '';

  const dualHat = scoped.some(
    (row) => (row.employee_no?.trim() ?? '') === departmentLeadPk && isEmptyPk(row.team_pk)
  );
  return dualHat ? departmentLeadPk : '';
}

export function resolveEmployeeName(rows: OrganisationStructure[], employeeNo: string): string {
  if (!employeeNo) return '';
  const match = rows.find((row) => (row.employee_no?.trim() ?? '') === employeeNo);
  const name = match?.employee_name?.trim();
  return name || `${employeeNo} (unknown)`;
}

export function resolveDepartmentLeadName(
  rows: OrganisationStructure[],
  departmentNo: string
): string {
  const pk = resolveDepartmentLeadPk(rows, departmentNo);
  return pk ? resolveEmployeeName(rows, pk) : '';
}

export function resolveTeamLeadName(
  rows: OrganisationStructure[],
  departmentNo: string,
  teamNo: string
): string {
  const pk = resolveTeamLeadPk(rows, departmentNo, teamNo);
  return pk ? resolveEmployeeName(rows, pk) : '';
}

export function pickRepresentativeRow(
  rows: OrganisationStructure[],
  departmentNo: string,
  teamNo: string,
  employeeNo: string
): OrganisationStructure | undefined {
  const matches = rows.filter(
    (row) =>
      (row.department_no?.trim() ?? '') === departmentNo &&
      (row.team_no?.trim() ?? '') === teamNo &&
      (row.employee_no?.trim() ?? '') === employeeNo
  );
  if (matches.length === 0) return undefined;
  return [...matches].sort((a, b) => {
    const roleDiff =
      (ROLE_STRENGTH[deriveDisplayRole(b)] ?? 0) - (ROLE_STRENGTH[deriveDisplayRole(a)] ?? 0);
    if (roleDiff !== 0) return roleDiff;
    return a.id.localeCompare(b.id);
  })[0];
}

export function pickRepresentativeRowForDepartment(
  rows: OrganisationStructure[],
  departmentNo: string,
  employeeNo: string
): OrganisationStructure | undefined {
  const matches = rows.filter(
    (row) =>
      (row.department_no?.trim() ?? '') === departmentNo &&
      (row.employee_no?.trim() ?? '') === employeeNo
  );
  if (matches.length === 0) return undefined;
  return [...matches].sort((a, b) => {
    const roleDiff =
      (ROLE_STRENGTH[deriveDisplayRole(b)] ?? 0) - (ROLE_STRENGTH[deriveDisplayRole(a)] ?? 0);
    if (roleDiff !== 0) return roleDiff;
    return a.id.localeCompare(b.id);
  })[0];
}

function upsertItem(
  items: Record<string, OrgPreviewItem>,
  item: OrgPreviewItem,
  parentId: string
): void {
  items[item.id] = item;
  const parent = items[parentId];
  if (parent && !parent.children.includes(item.id)) {
    parent.children.push(item.id);
  }
}

function attachEmployeesToParent(
  items: Record<string, OrgPreviewItem>,
  activeRows: OrganisationStructure[],
  parentId: string,
  departmentNo: string,
  scopeRows: OrganisationStructure[],
  teamNo: string | undefined,
  pickRepresentative: (employeeNo: string) => OrganisationStructure | undefined
): void {
  const employees = new Map<string, OrganisationStructure[]>();
  for (const row of scopeRows) {
    const employeeNo = row.employee_no?.trim() ?? '';
    if (!employeeNo) continue;
    const bucket = employees.get(employeeNo) ?? [];
    bucket.push(row);
    employees.set(employeeNo, bucket);
  }

  const sortedEmployees = [...employees.entries()].sort(
    ([employeeNoA, rowsA], [employeeNoB, rowsB]) => {
      const roleA = mergeRoles(rowsA.map(deriveDisplayRole));
      const roleB = mergeRoles(rowsB.map(deriveDisplayRole));
      const roleDiff = (ROLE_STRENGTH[roleB] ?? 0) - (ROLE_STRENGTH[roleA] ?? 0);
      if (roleDiff !== 0) return roleDiff;
      return employeeNoA.localeCompare(employeeNoB);
    }
  );

  for (const [employeeNo, employeeRows] of sortedEmployees) {
    const derivedRole = mergeRoles(employeeRows.map(deriveDisplayRole));
    const representative = pickRepresentative(employeeNo);
    const displayName = resolveEmployeeName(activeRows, employeeNo);
    const employeeType = employeeRows.find((row) => row.type?.trim())?.type?.trim();
    const specialRole = employeeRows.find((row) => row.special_role?.trim())?.special_role?.trim();
    const employeeId = teamNo
      ? `emp-${departmentNo}-${teamNo}-${employeeNo}`
      : `emp-${departmentNo}--${employeeNo}`;

    upsertItem(
      items,
      {
        id: employeeId,
        name: displayName,
        children: [],
        nodeType: 'employee',
        derivedRole: derivedRole || undefined,
        employeeType,
        specialRole,
        sourceRowId: representative?.id,
      },
      parentId
    );
  }
}

export function buildOrganisationPreviewTree(
  rows: OrganisationStructure[],
  options: BuildOrganisationPreviewOptions = {}
): OrganisationPreviewTree {
  const asOf = options.asOf ?? new Date();
  const activeRows = options.includeInactive ? rows : rows.filter((row) => isRowActive(row, asOf));
  const inactiveRowCount = options.includeInactive ? 0 : rows.length - activeRows.length;

  const items: Record<string, OrgPreviewItem> = {
    root: {
      id: 'root',
      name: 'Company',
      children: [],
      nodeType: 'root',
    },
  };

  const departments = new Map<string, string>();
  for (const row of activeRows) {
    const departmentNo = row.department_no?.trim() ?? '';
    if (!departmentNo) continue;
    if (!departments.has(departmentNo)) {
      departments.set(departmentNo, row.department_name?.trim() || departmentNo);
    }
  }

  const sortedDepartments = [...departments.entries()].sort(([a], [b]) => compareSortKey(a, b));

  for (const [departmentNo, departmentName] of sortedDepartments) {
    const departmentLead = resolveDepartmentLeadName(activeRows, departmentNo);

    upsertItem(
      items,
      {
        id: `dept-${departmentNo}`,
        name: departmentName,
        leadName: departmentLead || undefined,
        children: [],
        nodeType: 'department',
        subtitle: departmentNo,
      },
      'root'
    );

    if (departmentHasTeams(departmentNo)) {
      const teams = new Map<string, string>();
      for (const row of activeRows) {
        if ((row.department_no?.trim() ?? '') !== departmentNo) continue;
        const teamNo = row.team_no?.trim() ?? '';
        if (!teamNo || isDirectorSeat(teamNo)) continue;
        if (!teams.has(teamNo)) {
          teams.set(teamNo, row.team_name?.trim() || teamNo);
        }
      }

      const sortedTeams = [...teams.entries()].sort(([a], [b]) => compareSortKey(a, b));

      for (const [teamNo, teamName] of sortedTeams) {
        const teamLead = resolveTeamLeadName(activeRows, departmentNo, teamNo);

        const teamId = `team-${departmentNo}-${teamNo}`;
        upsertItem(
          items,
          {
            id: teamId,
            name: `${teamName} Team`,
            leadName: teamLead || undefined,
            children: [],
            nodeType: 'team',
            subtitle: teamNo,
          },
          `dept-${departmentNo}`
        );

        const teamRows = activeRows.filter(
          (row) =>
            (row.department_no?.trim() ?? '') === departmentNo &&
            (row.team_no?.trim() ?? '') === teamNo
        );

        attachEmployeesToParent(
          items,
          activeRows,
          teamId,
          departmentNo,
          teamRows,
          teamNo,
          (employeeNo) => pickRepresentativeRow(activeRows, departmentNo, teamNo, employeeNo)
        );
      }
    } else {
      const deptRows = activeRows.filter(
        (row) => (row.department_no?.trim() ?? '') === departmentNo
      );

      attachEmployeesToParent(
        items,
        activeRows,
        `dept-${departmentNo}`,
        departmentNo,
        deptRows,
        undefined,
        (employeeNo) => pickRepresentativeRowForDepartment(activeRows, departmentNo, employeeNo)
      );
    }
  }

  return {
    rootItemId: 'root',
    items,
    activeRowCount: activeRows.length,
    inactiveRowCount,
  };
}

// ponytail: self-check — fails if role derivation or dual-hat lead Presolution regresses
function runSelfCheck(): void {
  const mdRow: OrganisationStructure = {
    id: '1',
    department_no: '0',
    team_no: '0_0',
    employee_no: 'alice',
    department_pk: 'alice',
    team_pk: '',
  };
  const pdRow: OrganisationStructure = {
    id: '2',
    department_no: '4',
    team_no: '4_0',
    employee_no: 'bob',
    department_pk: 'bob',
    team_pk: '-',
  };
  const tlRow: OrganisationStructure = {
    id: '3',
    department_no: '10',
    team_no: '10_1',
    employee_no: 'carol',
    team_pk: 'carol',
    department_pk: 'carol',
  };
  const dualHatRows: OrganisationStructure[] = [
    {
      id: '4',
      department_no: '10',
      team_no: '10_0',
      employee_no: 'carol',
      department_pk: 'carol',
      team_pk: '',
    },
    {
      id: '5',
      department_no: '10',
      team_no: '10_1',
      employee_no: 'carol',
      department_pk: 'carol',
      team_pk: '',
    },
    {
      id: '6',
      department_no: '10',
      team_no: '10_1',
      employee_no: 'dave',
      employee_name: 'Dave',
    },
  ];

  console.assert(deriveDisplayRole(mdRow) === DISPLAY_ROLES.MANAGING_DIRECTOR, 'MD role');
  console.assert(deriveDisplayRole(pdRow) === DISPLAY_ROLES.PORTFOLIO_DIRECTOR, 'PD role');
  console.assert(deriveDisplayRole(tlRow) === DISPLAY_ROLES.TEAM_LEAD, 'TL role');
  console.assert(isEmptyPk('-') && isEmptyPk('  '), 'empty PK');
  console.assert(resolveTeamLeadPk(dualHatRows, '10', '10_1') === 'carol', 'dual-hat team lead');
  console.assert(departmentHasTeams('10'), 'dept 10 has teams');
  console.assert(!departmentHasTeams('4'), 'dept 4 is flat');

  const tree = buildOrganisationPreviewTree([...dualHatRows, mdRow, pdRow, tlRow], {
    includeInactive: true,
  });
  console.assert(tree.items['dept-10']?.children.length === 1, 'one team under dept 10');
  console.assert(
    tree.items['dept-10']?.children[0]?.startsWith('team-'),
    'dept 10 child is a team'
  );
  console.assert(
    !Object.keys(tree.items).some((id) => id.startsWith('team-4-')),
    'dept 4 has no team nodes'
  );
  console.assert(
    tree.items['dept-4']?.children.includes('emp-4--bob'),
    'dept 4 lists bob directly'
  );
  console.assert(
    tree.items['dept-0']?.children.includes('emp-0--alice'),
    'dept 0 lists alice directly'
  );
}

if (import.meta.env.DEV) {
  runSelfCheck();
}
